import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import LangSwitcher from "@/components/LangSwitcher";
import RatchetLogo from "@/components/RatchetLogo";
import OTPInput from "@/components/OTPInput";
import TotpEnroll from "@/components/TotpEnroll";

export default function Login() {
  const t = useT();
  const { login, loginTotp } = useAuth();
  const nav = useNavigate();

  const [stage, setStage] = useState("form");   // form → totp_verify | totp_setup
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [preauth, setPreauth] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const data = await login(email, password);
      if (data.token) { nav("/app"); return; }
      setPreauth(data.preauth_token);
      setStage(data.totp_setup_required ? "totp_setup" : "totp_verify");
    } catch (e) {
      setErr(e?.response?.data?.detail || t("auth.invalidCreds"));
    } finally { setLoading(false); }
  };

  const verifyTotp = async (value) => {
    const c = value ?? code;
    setErr(""); setLoading(true);
    try {
      await loginTotp(preauth, c);
      nav("/app");
    } catch (e) {
      setErr(e?.response?.data?.detail || t("auth.totp.err.invalid"));
      setCode("");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between p-12 w-1/2 border-r border-white/5 bg-gradient-to-br from-black via-[#050A1A] to-black">
        <Link to="/" className="flex items-center gap-2">
          <RatchetLogo size={28} />
          <span className="font-display font-black tracking-tight">RATCHET</span>
        </Link>
        <div>
          <div className="overline mb-3">{t("auth.signin.testimonialFrom")}</div>
          <p className="font-display text-2xl tracking-tight leading-snug max-w-md">{t("auth.signin.testimonial")}</p>
          <p className="text-sm text-white/40 mt-4 font-mono">@platform-engineer · payments squad</p>
        </div>
        <div className="overline">v1.0 · 2026</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6"><LangSwitcher /></div>

        {stage === "form" && (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
            <div>
              <div className="overline mb-2">{t("auth.welcomeBack")}</div>
              <h1 className="font-display font-black text-3xl tracking-tighter">{t("auth.signinTo")}</h1>
            </div>
            <div>
              <label className="overline block mb-1.5">{t("common.email")}</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none font-mono"
                placeholder="you@company.com" data-testid="login-email-input" />
            </div>
            <div>
              <label className="overline block mb-1.5">{t("common.password")}</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none font-mono"
                data-testid="login-password-input" />
            </div>
            {err && <div className="text-sm text-destructive font-mono" data-testid="login-error">{err}</div>}
            <button disabled={loading} className="btn-primary w-full" data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.signin.cta")}
            </button>
            <p className="text-sm text-white/40 text-center">
              {t("auth.noAccount")} <Link to="/signup" className="text-primary hover:underline" data-testid="login-link-signup">{t("auth.startFree")}</Link>
            </p>
          </form>
        )}

        {stage === "totp_verify" && (
          <div className="w-full max-w-sm space-y-5" data-testid="login-totp">
            <div>
              <div className="overline mb-2">{t("auth.welcomeBack")}</div>
              <h1 className="font-display font-black text-3xl tracking-tighter">{t("auth.totp.loginTitle")}</h1>
              <p className="text-sm text-white/50 mt-2">{t("auth.totp.loginSubtitle")}</p>
            </div>
            <OTPInput value={code} onChange={setCode} onComplete={verifyTotp} disabled={loading} error={!!err} />
            {err && <div className="text-sm text-destructive font-mono text-center" data-testid="login-totp-error">{err}</div>}
            <button disabled={loading || code.length < 6} onClick={() => verifyTotp()} className="btn-primary w-full" data-testid="login-totp-verify">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.totp.verify")}
            </button>
          </div>
        )}

        {stage === "totp_setup" && (
          <div className="w-full max-w-sm space-y-4">
            <p className="text-sm text-amber-400/80 font-mono" data-testid="login-totp-forced">{t("auth.totp.required")}</p>
            <TotpEnroll preauth={preauth} onDone={() => nav("/app")} />
          </div>
        )}
      </div>
    </div>
  );
}
